import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent
} from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  CircleStop,
  FlaskConical,
  GitFork,
  GitMerge,
  Inbox,
  Maximize2,
  Minimize2,
  Network
} from "lucide-react";
import type {
  CandidateExperimentDraft,
  GuidanceItemView,
  GuidancePublishInput,
  ProposalLineageProjection,
  ResearchLineageNodeProjection,
  ResearchNodeRef
} from "@roboscientist/contracts";
import { api } from "../api.js";
import { CandidateStudio, GuidanceInboxInspector } from "./ProposalInteractionPanels.js";
import {
  clampLineageInspectorWidth,
  lineageForkChildCounts,
  lineageInspectorDefaultWidth,
  lineageInspectorMaximumWidth,
  lineageInspectorMinimumWidth,
  lineageNodeDomId,
  lineageNodeDiameter,
  lineageNodeIsPruned,
  lineageResizeHandleWidth,
  lineageRefKey,
  layoutResearchLineage
} from "./researchLineageLayout.js";
import { summarizeLineageNodeRuns } from "./researchLineageEvidence.js";

const lineageInspectorWidthStorageKey = "roboscientist.cockpit.lineage-inspector-width";

const outcomeLabel = {
  passed: "Passed",
  failed: "Failed",
  improved: "Improved",
  not_improved: "Not improved",
  inconclusive: "Inconclusive",
  invalid: "Invalid evidence"
} as const;

const runIsActive = (status: string): boolean =>
  status.toLowerCase() === "running" || status.toLowerCase() === "active";

const runHistoryIssueLabel = (
  summary: ReturnType<typeof summarizeLineageNodeRuns>
): string => [
  summary.failedCount ? `${summary.failedCount} failed` : "",
  summary.interruptedCount ? `${summary.interruptedCount} interrupted` : "",
  summary.descriptorWarningCount ? `${summary.descriptorWarningCount} descriptor` : ""
].filter(Boolean).join(" · ");

const record = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};

const acceptedInboxCandidate = (item: GuidanceItemView): CandidateExperimentDraft | null => {
  if (item.kind !== "candidate_accepted" || item.state === "applied" || !item.targetNodeRef) return null;
  const raw = record(item.payload.candidate);
  const planner = record(item.payload.planner);
  const title = typeof raw.title === "string" ? raw.title : "";
  const key = typeof raw.key === "string" ? raw.key : item.id;
  const directionPath = Array.isArray(raw.directionPath)
    ? raw.directionPath.filter((value): value is string => typeof value === "string" && Boolean(value.trim()))
    : [];
  const primaryParentRef = record(raw.primaryParentRef) as ResearchNodeRef;
  if (!title || !directionPath.length || !primaryParentRef.proposalId || !primaryParentRef.experimentId || !primaryParentRef.nodeId) return null;
  return {
    id: `inbox-${item.id}`,
    key,
    plannerSessionId: typeof planner.sessionId === "string" ? planner.sessionId : "inbox",
    proposalId: item.proposalId,
    sourceNodeRef: item.targetNodeRef,
    title,
    directionPath,
    hypothesis: typeof raw.hypothesis === "string" ? raw.hypothesis : "",
    intervention: typeof raw.intervention === "string" ? raw.intervention : "",
    decisionRule: typeof raw.decisionRule === "string" ? raw.decisionRule : "",
    structuredCriteria: raw.structuredCriteria && typeof raw.structuredCriteria === "object" && !Array.isArray(raw.structuredCriteria)
      ? raw.structuredCriteria as Record<string, unknown>
      : null,
    primaryParentRef,
    secondaryParentRefs: Array.isArray(raw.secondaryParentRefs) ? raw.secondaryParentRefs as ResearchNodeRef[] : [],
    comparatorRef: raw.comparatorRef && typeof raw.comparatorRef === "object" ? raw.comparatorRef as ResearchNodeRef : null,
    rationale: typeof raw.rationale === "string" ? raw.rationale : "Accepted Candidate Planner draft.",
    state: "accepted",
    acceptedGuidanceItemId: item.id
  };
};

function NodeInspector({
  node,
  lineageEmpty = false,
  replaying = false,
  onStopDirection,
  onStopActiveRuns,
  actionError = null,
  actionPending = false,
  readOnly = false
}: {
  node: ResearchLineageNodeProjection | null;
  lineageEmpty?: boolean;
  replaying?: boolean;
  onStopDirection: (node: ResearchLineageNodeProjection) => void;
  onStopActiveRuns: (runIds: string[]) => void;
  actionError?: string | null;
  actionPending?: boolean;
  readOnly?: boolean;
}) {
  if (!node) return <aside className="lineage-inspector lineage-inspector--empty">
    <Network size={22} /><strong>{lineageEmpty ? "No formal node to inspect" : "Select an experiment node"}</strong><p>{lineageEmpty ? "The inspector will open when an Agent records the first lineage node." : "Inspect its hypothesis, change, rule and Agent assessment."}</p>
  </aside>;
  const assessment = node.latestAssessment;
  const selection = node.currentSelection;
  const rule = node.decisionRules.at(-1) ?? null;
  const runSummary = summarizeLineageNodeRuns(node);
  const activeRunIds = node.runEvidence
    .filter(({ run }) => runIsActive(run.lifecycle.status || run.status))
    .map(({ run }) => run.id);
  return <aside className="lineage-inspector" aria-label={`Inspector for ${node.title}`}>
    <header>
      <span className="kicker">NODE INSPECTOR</span>
      <h3>{node.title}</h3>
      {replaying ? <span className="lineage-replay-inspector-state"><i /> Exploring saved step</span> : null}
      <div className="lineage-inspector__badges">
        <span className={`lineage-outcome lineage-outcome--${assessment?.outcome ?? "unassessed"}`}>Assessment · {assessment ? outcomeLabel[assessment.outcome] : "Unassessed"}</span>
        <span className={`lineage-selection lineage-selection--${selection?.state ?? "none"}`}>Selection · {selection?.state ?? "none"}</span>
        {!node.directionPath.length ? <span className="lineage-classification">Needs classification</span> : null}
        {runSummary.assessmentReviewCount ? <span className="lineage-assessment-review">Review suggested · {runSummary.assessmentReviewCount} Run{runSummary.assessmentReviewCount === 1 ? "" : "s"}</span> : null}
        {runSummary.historyIssueCount ? <span className="lineage-run-history">Run history · {runSummary.historyIssueCount} issue{runSummary.historyIssueCount === 1 ? "" : "s"}</span> : null}
      </div>
    </header>

    <section><h4>Direction</h4><p>{node.directionPath.join(" / ") || "Needs classification"}</p></section>
    <section><h4>Hypothesis</h4><p>{node.hypothesis}</p></section>
    <section><h4>Change</h4><p>{node.intervention}</p></section>
    <section>
      <h4>Rule</h4>
      {rule ? <><p>{rule.statement}</p>{rule.structuredCriteria ? <pre>{JSON.stringify(rule.structuredCriteria, null, 2)}</pre> : null}</> : <p className="lineage-muted">No current rule is recorded.</p>}
    </section>
    <section>
      <h4>Assessment</h4>
      {assessment ? <p>{assessment.rationale}</p> : <p className="lineage-muted">No Agent assessment. Metrics do not create a conclusion automatically.</p>}
    </section>
    <section>
      <h4>Run history</h4>
      <p>{node.runEvidence.length} bound Run{node.runEvidence.length === 1 ? "" : "s"}. {runSummary.historyIssueCount ? runHistoryIssueLabel(runSummary) : "No lifecycle or descriptor issues recorded."}</p>
      {runSummary.assessmentReviewCount ? <small>{runSummary.assessmentReviewCount} terminal formal Run{runSummary.assessmentReviewCount === 1 ? "" : "s"} have not been referenced by an explicit assessment.</small> : null}
    </section>

    {!readOnly ? <section className="lineage-agent-actions"><h4>Agent communication</h4><div>
      <button type="button" className="button" disabled={actionPending || !node.directionPath.length} onClick={() => onStopDirection(node)}><CircleStop size={12} /> Stop expanding direction</button>
    </div><small>Stops future expansion of this direction. Active Runs remain independent.</small>
      {activeRunIds.length ? <div className="lineage-run-control"><small>Independent high-impact operation</small><button type="button" className="button is-dangerous" disabled={actionPending} onClick={() => {
        if (window.confirm(`Stop ${activeRunIds.length} active Run${activeRunIds.length === 1 ? "" : "s"}? This does not change the direction guidance or delete evidence.`)) onStopActiveRuns(activeRunIds);
      }}><CircleStop size={12} /> Stop active Runs · {activeRunIds.length}</button></div> : null}
      {actionError ? <span className="lineage-action-error">{actionError}</span> : null}
    </section> : null}
  </aside>;
}

export type LineageReplayPresentation = {
  entryProgress?: ReadonlyMap<string, number>;
  canvasScale?: number;
  cameraProgress?: number;
  instant?: boolean;
  visibleKeys: ReadonlySet<string>;
  activeKeys: ReadonlySet<string>;
  nodeDetails: ReadonlyMap<string, ResearchLineageNodeProjection>;
  focusKey: string | null;
  depth: number;
  stageProgress: number;
  playing: boolean;
  complete: boolean;
  onInspect: () => void;
};

export function ResearchLineageMap({ lineage, readOnly = false, replay: requestedReplay }: {
  lineage: ProposalLineageProjection;
  readOnly?: boolean;
  replay?: LineageReplayPresentation;
}) {
  const replay = readOnly ? requestedReplay : undefined;
  const queryClient = useQueryClient();
  const nodes = useMemo(() => new Map(lineage.nodes.map((node) => [lineageRefKey(node.ref), node])), [lineage.nodes]);
  const workspaceRef = useRef<HTMLDivElement>(null);
  const canvasScrollRef = useRef<HTMLDivElement>(null);
  const canvasResizeFrameRef = useRef<number | null>(null);
  const [canvasViewportWidth, setCanvasViewportWidth] = useState(0);
  const [workspaceViewportWidth, setWorkspaceViewportWidth] = useState(0);
  const [inspectorWidth, setInspectorWidth] = useState(() => {
    if (typeof window === "undefined") return lineageInspectorDefaultWidth;
    try {
      const stored = window.localStorage.getItem(lineageInspectorWidthStorageKey);
      return clampLineageInspectorWidth(stored === null ? lineageInspectorDefaultWidth : Number(stored), 0);
    } catch {
      return lineageInspectorDefaultWidth;
    }
  });
  const preferredInspectorWidthRef = useRef(inspectorWidth);
  const liveInspectorWidthRef = useRef(inspectorWidth);
  const resizeFrameRef = useRef<number | null>(null);
  const pendingResizeClientXRef = useRef<number | null>(null);
  const resizePointerIdRef = useRef<number | null>(null);
  const resizingRef = useRef(false);
  const [isResizingInspector, setIsResizingInspector] = useState(false);
  const layout = useMemo(() => layoutResearchLineage(lineage.nodes, { viewportWidth: canvasViewportWidth * (replay?.canvasScale ?? 1) }), [canvasViewportWidth, lineage.nodes, replay?.canvasScale]);
  const championKeys = useMemo(() => new Set(lineage.championLineageRefs.map(lineageRefKey)), [lineage.championLineageRefs]);
  const forkChildCounts = useMemo(() => lineageForkChildCounts(lineage.nodes), [lineage.nodes]);
  const prunedKeys = useMemo(() => new Set(lineage.nodes.filter(lineageNodeIsPruned).map((node) => lineageRefKey(node.ref))), [lineage.nodes]);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const replayFocusRef = useRef<string | null>(null);
  const [canvasExpanded, setCanvasExpanded] = useState(false);
  const [inspectorMode, setInspectorMode] = useState<"node" | "planner" | "inbox">("node");
  const [plannerId, setPlannerId] = useState<string | null>(null);
  const [selectedCandidateId, setSelectedCandidateId] = useState<string | null>(null);
  const lineageRevision = useMemo(() => Object.fromEntries(lineage.experiments.map((experiment) => [
    experiment.experimentId,
    experiment.revision
  ])), [lineage.experiments]);
  const inboxQuery = useQuery({
    queryKey: ["guidance-inbox", lineage.proposalId],
    queryFn: () => api.guidanceInbox(lineage.proposalId),
    enabled: !readOnly,
    refetchInterval: 10_000
  });
  const plannerQuery = useQuery({
    queryKey: ["candidate-planner", plannerId],
    queryFn: () => api.candidatePlanner(plannerId!),
    enabled: !readOnly && Boolean(plannerId),
    refetchInterval: (query) => !query.state.data || query.state.data.session.state === "running" ? 1_500 : false
  });
  const setPlannerResult = (result: Awaited<ReturnType<typeof api.candidatePlanner>>) => {
    setPlannerId(result.session.id);
    queryClient.setQueryData(["candidate-planner", result.session.id], result);
  };
  const createPlanner = useMutation({
    mutationFn: async (sourceNodeRef: ResearchNodeRef) => {
      if (plannerId) await api.closeCandidatePlanner(plannerId).catch(() => undefined);
      return api.createCandidatePlanner(lineage.proposalId, sourceNodeRef);
    },
    onSuccess: setPlannerResult
  });
  const discussPlanner = useMutation({
    mutationFn: (message: string) => api.discussCandidatePlanner(plannerId!, message),
    onSuccess: setPlannerResult
  });
  const acceptCandidate = useMutation({
    mutationFn: (candidateId: string) => api.acceptCandidate(plannerId!, candidateId),
    onSuccess: (result) => {
      setPlannerResult(result);
      void queryClient.invalidateQueries({ queryKey: ["guidance-inbox", lineage.proposalId] });
    }
  });
  const rejectCandidate = useMutation({
    mutationFn: (candidateId: string) => api.rejectCandidate(plannerId!, candidateId),
    onSuccess: setPlannerResult
  });
  const publishGuidance = useMutation({
    mutationFn: (input: GuidancePublishInput) => api.publishInboxGuidance(lineage.proposalId, input),
    onSuccess: () => {
      setInspectorMode("inbox");
      void queryClient.invalidateQueries({ queryKey: ["guidance-inbox", lineage.proposalId] });
    }
  });
  const applyNow = useMutation({
    mutationFn: api.applyInboxGuidanceNow,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["guidance-inbox", lineage.proposalId] })
  });
  const rejectGuidance = useMutation({
    mutationFn: (itemId: string) => api.rejectInboxGuidance(itemId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["guidance-inbox", lineage.proposalId] })
  });
  const stopActiveRuns = useMutation({
    mutationFn: async (runIds: string[]) => Promise.all(runIds.map((runId) => api.stopActiveRun(runId))),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["proposal-lineage", lineage.proposalId] });
      void queryClient.invalidateQueries({ queryKey: ["topic-experiments"] });
      void queryClient.invalidateQueries({ queryKey: ["experiments"] });
    }
  });
  const plannerSession = plannerQuery.data?.session ?? createPlanner.data?.session ?? null;
  const plannerError = [
    createPlanner.error,
    plannerQuery.error,
    discussPlanner.error,
    acceptCandidate.error,
    rejectCandidate.error
  ].find(Boolean)?.message ?? null;
  const inbox = inboxQuery.data?.inbox ?? null;
  const interactionPending = createPlanner.isPending || discussPlanner.isPending
    || acceptCandidate.isPending || rejectCandidate.isPending || publishGuidance.isPending
    || applyNow.isPending || rejectGuidance.isPending || stopActiveRuns.isPending;
  const ghostCandidates = useMemo(() => {
    const appliedItemIds = new Set((inbox?.items ?? [])
      .filter((item) => item.state === "applied")
      .map((item) => item.id));
    const values = (plannerSession?.candidates ?? []).filter((candidate) =>
      !candidate.acceptedGuidanceItemId || !appliedItemIds.has(candidate.acceptedGuidanceItemId)
    );
    const acceptedItemIds = new Set(values.map((candidate) => candidate.acceptedGuidanceItemId).filter(Boolean));
    for (const item of inbox?.items ?? []) {
      if (acceptedItemIds.has(item.id)) continue;
      const candidate = acceptedInboxCandidate(item);
      if (candidate) values.push(candidate);
    }
    return values;
  }, [inbox?.items, plannerSession?.candidates]);
  const ghostPositions = useMemo(() => {
    const formalPositions = new Map(layout.nodes.map((position) => [position.key, position]));
    return ghostCandidates.map((candidate, index) => {
      const parent = formalPositions.get(lineageRefKey(candidate.primaryParentRef))
        ?? formalPositions.get(lineageRefKey(candidate.sourceNodeRef));
      const nextGeneration = parent ? layout.generations.find((generation) => generation.depth === parent.depth + 1) : null;
      const x = parent
        ? (nextGeneration?.centerX ?? parent.x + 100) - lineageNodeDiameter / 2 + (index - (ghostCandidates.length - 1) / 2) * 8
        : layout.width - 100;
      const y = Math.max(44, Math.min(
        layout.height - lineageNodeDiameter - 12,
        (parent?.y ?? 70) + (index - (ghostCandidates.length - 1) / 2) * 28
      ));
      return { candidate, parent, x, y, depth: (parent?.depth ?? layout.maxDepth) + 1 };
    });
  }, [ghostCandidates, layout]);

  const setActualInspectorWidth = (requestedWidth: number, workspaceWidth?: number): number => {
    const measuredWorkspaceWidth = workspaceWidth
      ?? workspaceRef.current?.getBoundingClientRect().width
      ?? 0;
    const nextWidth = clampLineageInspectorWidth(requestedWidth, measuredWorkspaceWidth);
    liveInspectorWidthRef.current = nextWidth;
    setInspectorWidth((currentWidth) => currentWidth === nextWidth ? currentWidth : nextWidth);
    return nextWidth;
  };
  const persistInspectorWidth = (width: number) => {
    preferredInspectorWidthRef.current = width;
    try {
      window.localStorage.setItem(lineageInspectorWidthStorageKey, String(width));
    } catch {
      // Storage availability must not affect resizing.
    }
  };
  const resizeInspectorToClientX = (clientX: number): number => {
    const workspace = workspaceRef.current;
    if (!workspace) return liveInspectorWidthRef.current;
    const bounds = workspace.getBoundingClientRect();
    return setActualInspectorWidth(
      bounds.right - clientX - lineageResizeHandleWidth / 2,
      bounds.width
    );
  };
  const scheduleInspectorResize = (clientX: number) => {
    pendingResizeClientXRef.current = clientX;
    if (resizeFrameRef.current !== null) return;
    resizeFrameRef.current = window.requestAnimationFrame(() => {
      resizeFrameRef.current = null;
      const pendingClientX = pendingResizeClientXRef.current;
      pendingResizeClientXRef.current = null;
      if (pendingClientX !== null) resizeInspectorToClientX(pendingClientX);
    });
  };
  const finishInspectorResize = (event: ReactPointerEvent<HTMLDivElement>, cancelled = false) => {
    if (resizePointerIdRef.current !== event.pointerId) return;
    if (resizeFrameRef.current !== null) window.cancelAnimationFrame(resizeFrameRef.current);
    resizeFrameRef.current = null;
    pendingResizeClientXRef.current = null;
    const nextWidth = cancelled
      ? liveInspectorWidthRef.current
      : resizeInspectorToClientX(event.clientX);
    persistInspectorWidth(nextWidth);
    resizePointerIdRef.current = null;
    resizingRef.current = false;
    setIsResizingInspector(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };
  const loseInspectorResizeCapture = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (resizePointerIdRef.current !== event.pointerId) return;
    if (resizeFrameRef.current !== null) window.cancelAnimationFrame(resizeFrameRef.current);
    resizeFrameRef.current = null;
    pendingResizeClientXRef.current = null;
    persistInspectorWidth(liveInspectorWidthRef.current);
    resizePointerIdRef.current = null;
    resizingRef.current = false;
    setIsResizingInspector(false);
  };
  const beginInspectorResize = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0 || window.matchMedia("(max-width: 1120px)").matches) return;
    event.preventDefault();
    resizePointerIdRef.current = event.pointerId;
    resizingRef.current = true;
    liveInspectorWidthRef.current = inspectorWidth;
    event.currentTarget.setPointerCapture(event.pointerId);
    setIsResizingInspector(true);
  };
  const moveInspectorResize = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (resizePointerIdRef.current !== event.pointerId) return;
    event.preventDefault();
    scheduleInspectorResize(event.clientX);
  };
  const commitInspectorWidth = (requestedWidth: number) => {
    const nextWidth = setActualInspectorWidth(requestedWidth);
    persistInspectorWidth(nextWidth);
  };
  const handleInspectorResizeKey = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    const step = event.shiftKey ? 64 : 24;
    const requestedWidth = event.key === "ArrowLeft"
      ? inspectorWidth + step
      : event.key === "ArrowRight"
        ? inspectorWidth - step
        : event.key === "Home"
          ? lineageInspectorMaximumWidth
          : event.key === "End"
            ? lineageInspectorMinimumWidth
            : null;
    if (requestedWidth === null) return;
    event.preventDefault();
    commitInspectorWidth(requestedWidth);
  };

  useEffect(() => {
    const element = workspaceRef.current;
    if (!element) return;
    const updateWorkspaceWidth = () => {
      const nextWorkspaceWidth = Math.max(0, Math.floor(element.clientWidth));
      setWorkspaceViewportWidth((currentWidth) => currentWidth === nextWorkspaceWidth ? currentWidth : nextWorkspaceWidth);
      if (!resizingRef.current) {
        setActualInspectorWidth(preferredInspectorWidthRef.current, nextWorkspaceWidth);
      }
    };
    updateWorkspaceWidth();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateWorkspaceWidth);
      return () => window.removeEventListener("resize", updateWorkspaceWidth);
    }
    const observer = new ResizeObserver(updateWorkspaceWidth);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    if (!isResizingInspector) return;
    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    return () => {
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
    };
  }, [isResizingInspector]);
  useEffect(() => () => {
    if (resizeFrameRef.current !== null) window.cancelAnimationFrame(resizeFrameRef.current);
    if (canvasResizeFrameRef.current !== null) window.cancelAnimationFrame(canvasResizeFrameRef.current);
  }, []);
  useLayoutEffect(() => {
    const element = canvasScrollRef.current;
    if (!element) return;
    const measureWidth = () => {
      // Layout uses CSS pixels; a scaled film/player must not enlarge the graph again.
      const nextWidth = Math.max(0, element.clientWidth);
      setCanvasViewportWidth((currentWidth) => currentWidth === nextWidth ? currentWidth : nextWidth);
    };
    const updateWidth = () => {
      if (canvasResizeFrameRef.current !== null) return;
      canvasResizeFrameRef.current = window.requestAnimationFrame(() => {
        canvasResizeFrameRef.current = null;
        measureWidth();
      });
    };
    measureWidth();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateWidth);
      return () => window.removeEventListener("resize", updateWidth);
    }
    const observer = new ResizeObserver(updateWidth);
    observer.observe(element);
    return () => {
      observer.disconnect();
      if (canvasResizeFrameRef.current !== null) window.cancelAnimationFrame(canvasResizeFrameRef.current);
      canvasResizeFrameRef.current = null;
    };
  }, []);
  useEffect(() => {
    if (replay) {
      const focusChanged = replayFocusRef.current !== replay.focusKey;
      replayFocusRef.current = replay.focusKey;
      if (replay.playing || focusChanged || !selectedKey || !replay.visibleKeys.has(selectedKey)) {
        setSelectedKey(replay.focusKey);
      }
      return;
    }
    if (selectedKey && nodes.has(selectedKey)) return;
    const initial = lineage.championRefs[0] ?? lineage.frontierRefs[0] ?? lineage.nodes[0]?.ref ?? null;
    setSelectedKey(initial ? lineageRefKey(initial) : null);
  }, [lineage.championRefs, lineage.frontierRefs, lineage.nodes, nodes, selectedKey, replay?.focusKey, replay?.playing, replay?.visibleKeys]);
  useLayoutEffect(() => {
    if (!replay) return;
    const canvas = canvasScrollRef.current;
    if (!canvas) return;
    if (replay.instant) {
      if (replay.cameraProgress != null) {
        // Fixed-frame presentation paints the camera transform below. Browser scroll
        // positions can lag behind a captured frame, especially at a larger scale.
        canvas.scrollLeft = 0;
        return;
      }
      const index = layout.generations.findIndex((band) => band.depth === replay.depth);
      const current = layout.generations[index];
      if (!current) return;
      const previous = layout.generations[Math.max(0, index - 1)]!;
      const target = (center: number) => Math.max(0, Math.min(canvas.scrollWidth - canvas.clientWidth, center - canvas.clientWidth * .65));
      const from = target(previous.centerX);
      const to = target(current.centerX);
      canvas.scrollLeft = from + (to - from) * replay.stageProgress;
      return;
    }
    const position = layout.nodes.find((node) => node.key === replay.focusKey);
    if (!position) return;
    canvas.scrollTo({ left: Math.max(0, position.x - canvas.clientWidth * .65), behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
  }, [replay?.focusKey, replay?.instant, replay?.cameraProgress, replay?.instant ? replay.depth : undefined, replay?.instant ? replay.stageProgress : undefined, layout.nodes, layout.generations]);
  useEffect(() => {
    if (!canvasExpanded) return;
    const previousOverflow = document.body.style.overflow;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setCanvasExpanded(false);
    };
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [canvasExpanded]);
  const selectedNode = selectedKey ? (replay ? replay.nodeDetails.get(selectedKey) : nodes.get(selectedKey)) ?? null : null;
  const stopDirection = (node: ResearchLineageNodeProjection) => publishGuidance.mutate({
    kind: "stop_expanding_direction",
    priority: "next_checkpoint",
    targetNodeRef: node.ref,
    targetDirectionId: node.directionPath.join(" / "),
    maxStalenessSeconds: 900,
    expectedLineageRevision: lineageRevision,
    payload: {
      directionPath: node.directionPath,
      sourceNodeRef: node.ref,
      requestedSelection: "stopped",
      reversible: true,
      activeRunPolicy: "Do not stop active Runs as part of this guidance."
    }
  });
  const closePlanner = () => {
    setInspectorMode("node");
    if (plannerId) void api.closeCandidatePlanner(plannerId).then(() => {
      void queryClient.invalidateQueries({ queryKey: ["candidate-planner", plannerId] });
    }).catch(() => undefined);
  };

  const champion = lineage.championRefs.map((ref) => {
    const key = lineageRefKey(ref);
    return replay ? (replay.activeKeys.has(key) ? undefined : replay.nodeDetails.get(key)) : nodes.get(key);
  }).find(Boolean);
  const visibleCount = replay ? replay.visibleKeys.size : lineage.nodes.length;
  const frontierCount = replay ? lineage.frontierRefs.filter((ref) => replay.visibleKeys.has(lineageRefKey(ref)) && !replay.activeKeys.has(lineageRefKey(ref))).length : lineage.frontierRefs.length;
  const replayBandIndex = replay ? layout.generations.findIndex((band) => band.depth === replay.depth) : -1;
  const replayBand = layout.generations[replayBandIndex];
  const previousReplayBand = layout.generations[replayBandIndex - 1];
  // Include the gap between generations, avoiding a position jump at each boundary.
  const sweepStart = previousReplayBand ? previousReplayBand.x + previousReplayBand.width : replayBand?.x ?? 0;
  const sweepEnd = replayBand ? replayBand.x + replayBand.width : sweepStart;
  const canvasHeight = Math.max(layout.height, ...ghostPositions.map((position) => position.y + 52));
  const canvasWidth = Math.max(layout.width, ...ghostPositions.map((position) => position.x + 110));
  const cameraPhase = Math.max(0, Math.min(1, ((replay?.cameraProgress ?? 0) - .25) / .68));
  const presentationCameraX = replay?.instant && replay.cameraProgress != null
    ? Math.max(0, canvasWidth - canvasViewportWidth) * cameraPhase * cameraPhase * (3 - 2 * cameraPhase)
    : 0;
  const showUnassignedFooter = !lineage.nodes.length || Boolean(lineage.unassignedRuns.length) || Boolean(lineage.orphanedRuns.length);
  const workspaceHeight = 43 + canvasHeight + (showUnassignedFooter || ghostCandidates.length ? 35 : 0);
  const inspectorMaximumForWorkspace = clampLineageInspectorWidth(
    lineageInspectorMaximumWidth,
    workspaceViewportWidth
  );
  return <section className={`proposal-lineage ${canvasExpanded ? "has-expanded-canvas" : ""} ${replay ? `has-replay ${replay.playing ? "is-replay-playing" : "is-replay-paused"} ${replay.complete ? "is-replay-complete" : ""}` : ""}`}>
    <header className="proposal-lineage__status">
      <div><span className="kicker">RESEARCH PROPOSAL</span><strong>{lineage.title}</strong><p>{lineage.objective ?? "No proposal objective is available from the root Chat."}</p></div>
      <div className="proposal-lineage__controls">{!readOnly ? <button type="button" className={`agent-inbox-button ${inbox?.pendingCount ? "has-pending" : ""}`} onClick={() => setInspectorMode("inbox")}><Inbox size={14} /> Agent Inbox · {inbox?.pendingCount ?? 0} pending</button> : null}<dl>
          <div><dt>Champion</dt><dd>{champion?.title ?? "Not selected"}</dd></div>
          <div><dt>Active frontier</dt><dd>{frontierCount || "None"}</dd></div>
          <div><dt>Lineage</dt><dd>{visibleCount} nodes · {lineage.experiments.length} experiments</dd></div>
        </dl></div>
    </header>
    {lineage.warnings.length ? <div className="lineage-warning" role="status"><AlertTriangle size={15} /><div><strong>{lineage.warnings.length} lineage integrity warning{lineage.warnings.length === 1 ? "" : "s"}</strong>{lineage.warnings.slice(0, 3).map((warning, index) => <span key={`${warning.code}:${index}`}>{warning.message}</span>)}</div></div> : null}

    {canvasExpanded ? <button type="button" className="lineage-canvas-backdrop" aria-label="Close expanded canvas" onClick={() => setCanvasExpanded(false)} /> : null}
    <div
      ref={workspaceRef}
      className={`lineage-workspace ${lineage.nodes.length ? "" : "is-empty"} ${isResizingInspector ? "is-resizing" : ""}`}
      style={{
        "--lineage-workspace-height": `${workspaceHeight}px`,
        "--lineage-inspector-width": `${inspectorWidth}px`
      } as CSSProperties}
    >
      <div className={`lineage-canvas-shell ${canvasExpanded ? "is-expanded" : ""}`} role={canvasExpanded ? "dialog" : undefined} aria-modal={canvasExpanded || undefined} aria-label={canvasExpanded ? "Expanded evolution canvas" : undefined}>
        <div className="lineage-canvas-legend">
          <div className="lineage-canvas-legend__key"><span><i className="is-passed" /> passed</span><span><i className="is-failed" /> failed</span><span><i className="is-improved" /> improved</span><span><i className="is-not-improved" /> not improved</span><span><i className="is-inconclusive" /> inconclusive</span><span><i className="is-running" /> running</span><span><i className="is-invalid" /> invalid assessment</span><span><i className="is-run-history" /> run history issue</span><span><i className="is-ghost" /> candidate ghost</span><span><GitFork size={12} /> fork</span><span><i className="is-pruned" /> pruned / stopped</span><span><GitMerge size={12} /> dashed = secondary parent</span></div>
          <button type="button" className="lineage-canvas-expand" onClick={() => setCanvasExpanded((expanded) => !expanded)}>{canvasExpanded ? <Minimize2 size={13} /> : <Maximize2 size={13} />}{canvasExpanded ? "Close canvas" : "Expand canvas"}</button>
        </div>
        <div ref={canvasScrollRef} className="lineage-canvas-scroll" onPointerDown={replay?.onInspect} style={replay?.instant && replay.cameraProgress != null ? { overflowX: "hidden" } : undefined}>
          <div className="lineage-canvas" style={{ width: canvasWidth, height: canvasHeight, transform: presentationCameraX ? `translateX(${-presentationCameraX}px)` : undefined }}>
            <div className="lineage-generation-axis" aria-hidden="true">{layout.generations.map((generation) => <span key={generation.depth} className={replay && !replay.complete ? generation.depth === replay.depth ? "is-replay-current" : generation.depth > replay.depth ? "is-replay-future" : "" : ""} style={{ left: generation.centerX }}>G{generation.depth}</span>)}</div>
            {replayBand && replay && !replay.complete ? <div className="lineage-replay-sweep" aria-hidden="true" style={{ left: sweepStart + (sweepEnd - sweepStart) * replay.stageProgress }}><span>G{replay.depth}</span></div> : null}
            {layout.lanes.map((lane) => <div key={lane.id} className="lineage-direction-lane" style={{ top: lane.y, height: lane.height }}>
              <i style={{ top: lane.centerY - lane.y }} />
            </div>)}
            {layout.lanes.map((lane) => <span
              key={`label:${lane.id}`}
              className="lineage-direction-lane__label"
              style={{ top: lane.centerY }}
              tabIndex={0}
              aria-label={`Direction: ${lane.directionFamily}`}
            >
              <strong>{lane.directionFamily}</strong>
              <span className="lineage-direction-lane__tooltip" role="tooltip">{lane.directionFamily}</span>
            </span>)}
            <svg width={canvasWidth} height={canvasHeight} aria-hidden="true">
              {layout.edges.filter((edge) => (!replay || (replay.visibleKeys.has(edge.parentKey) && replay.visibleKeys.has(edge.childKey))) && (!edge.secondary || edge.childKey === selectedKey)).map((edge) => {
                const championEdge = championKeys.has(edge.parentKey) && championKeys.has(edge.childKey) && (!replay || (!replay.activeKeys.has(edge.parentKey) && !replay.activeKeys.has(edge.childKey)));
                const forkEdge = (forkChildCounts.get(edge.parentKey) ?? 0) > 1;
                const prunedEdge = replay ? [edge.parentKey, edge.childKey].some((key) => { const node = replay.nodeDetails.get(key); return node ? lineageNodeIsPruned(node) : false; }) : prunedKeys.has(edge.parentKey) || prunedKeys.has(edge.childKey);
                const bend = Math.max(32, Math.min(70, (edge.x2 - edge.x1) / 2));
                const sameLane = Math.abs(edge.y2 - edge.y1) < 1;
                const controlY1 = sameLane ? edge.y1 + edge.curveOffset : edge.y1;
                const controlY2 = sameLane ? edge.y2 + edge.curveOffset : edge.y2;
                return <path key={edge.id} style={replay?.entryProgress ? { clipPath: `inset(0 ${(1 - (replay.entryProgress.get(edge.childKey) ?? 1)) * 100}% 0 0)` } : undefined} className={`lineage-edge ${replay ? "is-replay-edge" : ""} ${edge.secondary ? "secondary" : "primary"} ${championEdge ? "champion" : ""} ${forkEdge ? "fork" : ""} ${prunedEdge ? "pruned" : ""}`} d={`M ${edge.x1} ${edge.y1} C ${edge.x1 + bend} ${controlY1}, ${edge.x2 - bend} ${controlY2}, ${edge.x2} ${edge.y2}`} />;
              })}
              {ghostPositions.map(({ candidate, parent, x, y }) => parent ? <path key={`ghost-edge:${candidate.id}`} className="ghost" d={`M ${parent.x + lineageNodeDiameter} ${parent.y + lineageNodeDiameter / 2} C ${parent.x + 42} ${parent.y + lineageNodeDiameter / 2}, ${x - 22} ${y + lineageNodeDiameter / 2}, ${x} ${y + lineageNodeDiameter / 2}`} /> : null)}
            </svg>
            {layout.nodes.filter((position) => !replay || replay.visibleKeys.has(position.key)).map((position) => {
              const node = replay?.nodeDetails.get(position.key) ?? nodes.get(position.key)!;
              const assessment = node.latestAssessment;
              const runSummary = summarizeLineageNodeRuns(node);
              const replayActive = replay?.activeKeys.has(position.key) ?? false;
              const active = replayActive || runSummary.activeCount > 0;
              const runHistoryIssue = runSummary.historyIssueCount > 0;
              const forkCount = forkChildCounts.get(position.key) ?? 0;
              const pruned = lineageNodeIsPruned(node);
              const classes = [
                "lineage-node",
                `lineage-node--${assessment?.outcome ?? "unassessed"}`,
                active ? "is-running" : "",
                replay ? "is-replay-revealed" : "",
                replayActive ? "is-replay-active" : "",
                runHistoryIssue ? "has-run-issues" : "",
                championKeys.has(position.key) && !replayActive ? "is-champion-lineage" : "",
                forkCount > 1 ? "is-fork" : "",
                pruned ? "is-pruned" : "",
                position.y > canvasHeight - 150 ? "tooltip-above" : "",
                selectedKey === position.key ? "is-selected" : ""
              ].filter(Boolean).join(" ");
              return <button
                type="button"
                key={position.key}
                id={lineageNodeDomId(position.key)}
                className={classes}
                style={{ left: position.x, top: position.y, width: lineageNodeDiameter, height: lineageNodeDiameter,
                  ...(replay?.entryProgress ? { opacity: (replay.entryProgress.get(position.key) ?? 1) * (pruned ? .68 : 1), transform: `scale(${.3 + .7 * (1 - (1 - (replay.entryProgress.get(position.key) ?? 1)) ** 3)})` } : {}) }}
                onClick={() => {
                  replay?.onInspect();
                  setSelectedKey(position.key);
                  setInspectorMode("node");
                }}
                aria-label={`Inspect ${node.title}${forkCount > 1 ? `, fork with ${forkCount} offspring` : ""}${pruned ? `, pruned ${node.currentSelection?.state}` : ""}`}
              >
                {active ? <i className="lineage-node__activity" /> : null}
                {pruned ? <span className="lineage-node__prune" aria-hidden="true" /> : null}
                <span className="lineage-node-tooltip" role="tooltip">
                  <span className="lineage-node-tooltip__meta"><code>G{position.depth}</code><small>{node.ref.experimentId}</small></span>
                  <strong>{node.title}</strong>
                  <span>{node.directionPath.join(" / ") || "Needs classification"}</span>
                  <span className="lineage-node-tooltip__facts"><small>Assessment · {assessment ? outcomeLabel[assessment.outcome] : "Unassessed"}</small><small>{node.runEvidence.length} run{node.runEvidence.length === 1 ? "" : "s"}</small></span>
                  {forkCount > 1 ? <em className="is-fork"><GitFork size={9} /> Fork · {forkCount} directions</em> : null}
                  {node.currentSelection ? <em className={pruned ? "is-pruned" : "is-selection"}>Selection · {node.currentSelection.state}</em> : null}
                  {runSummary.assessmentReviewCount ? <em className="is-assessment"><AlertTriangle size={9} /> Assessment review suggested · {runSummary.assessmentReviewCount} Run{runSummary.assessmentReviewCount === 1 ? "" : "s"}</em> : null}
                  {runHistoryIssue ? <em className="is-run-history"><AlertTriangle size={9} /> Run history · {runHistoryIssueLabel(runSummary)}</em> : null}
                </span>
              </button>;
            })}
            {ghostPositions.map(({ candidate, x, y, depth }) => <button
              type="button"
              key={`ghost:${candidate.id}`}
              className={`lineage-node is-ghost is-${candidate.state} ${selectedCandidateId === candidate.id ? "is-selected" : ""}`}
              style={{ left: x, top: y, width: lineageNodeDiameter, height: lineageNodeDiameter }}
              onClick={() => {
                setSelectedCandidateId(candidate.id);
                setInspectorMode(plannerSession && candidate.plannerSessionId === plannerSession.id ? "planner" : "inbox");
              }}
              aria-label={`Inspect ghost candidate ${candidate.title}, ${candidate.state}`}
            >
              <span className="lineage-node-tooltip" role="tooltip">
                <span className="lineage-node-tooltip__meta"><code>G{depth}</code><small>GHOST · {candidate.state}</small></span>
                <strong>{candidate.title}</strong>
                <span>{candidate.directionPath.join(" / ")}</span>
                <span className="lineage-node-tooltip__facts"><small>Candidate Planner draft</small><small>{candidate.state}</small></span>
              </span>
            </button>)}
            {!lineage.nodes.length ? <div className="lineage-canvas-empty">
              <Network size={29} />
              <span>GENERATION 0 · AWAITING FIRST DECLARATION</span>
              <h3>No formal lineage nodes yet</h3>
              <p>The Observer found {lineage.unassignedRuns.length} unbound Run{lineage.unassignedRuns.length === 1 ? "" : "s"}. They remain visible as evidence below, but are not promoted into hypotheses, parents, or assessments automatically.</p>
            </div> : null}
          </div>
        </div>
        {showUnassignedFooter || ghostCandidates.length ? <footer className="lineage-unassigned"><FlaskConical size={13} /><span>{visibleCount} formal nodes</span>{ghostCandidates.length ? <span>{ghostCandidates.length} ghost candidate{ghostCandidates.length === 1 ? "" : "s"}</span> : null}{replay && !replay.complete ? <span>Saved research replay</span> : <><span>{lineage.unassignedRuns.length} unbound Runs</span>{lineage.orphanedRuns.length ? <span>{lineage.orphanedRuns.length} broken bindings</span> : null}</>}</footer> : null}
      </div>
      <div
        className="lineage-resize-handle"
        role="separator"
        aria-label="Resize node map and experiment results"
        aria-orientation="vertical"
        aria-valuemin={lineageInspectorMinimumWidth}
        aria-valuemax={inspectorMaximumForWorkspace}
        aria-valuenow={inspectorWidth}
        aria-valuetext={`${inspectorWidth} pixels for experiment results`}
        aria-hidden={canvasExpanded || undefined}
        tabIndex={canvasExpanded ? -1 : 0}
        title="Drag to resize · Arrow keys to adjust · Double-click to reset"
        onPointerDown={beginInspectorResize}
        onPointerMove={moveInspectorResize}
        onPointerUp={(event) => finishInspectorResize(event)}
        onPointerCancel={(event) => finishInspectorResize(event, true)}
        onLostPointerCapture={loseInspectorResizeCapture}
        onKeyDown={handleInspectorResizeKey}
        onDoubleClick={() => commitInspectorWidth(lineageInspectorDefaultWidth)}
      ><span aria-hidden="true" /></div>
      {inspectorMode === "planner" ? <CandidateStudio
        session={plannerSession}
        loading={createPlanner.isPending || plannerQuery.isLoading}
        error={plannerError}
        selectedCandidateId={selectedCandidateId}
        onSelectCandidate={setSelectedCandidateId}
        onDiscuss={(message) => discussPlanner.mutate(message)}
        onAccept={(candidateId) => acceptCandidate.mutate(candidateId)}
        onReject={(candidateId) => rejectCandidate.mutate(candidateId)}
        onClose={closePlanner}
        mutating={interactionPending}
      /> : inspectorMode === "inbox" ? <GuidanceInboxInspector
        inbox={inbox}
        loading={inboxQuery.isLoading}
        error={inboxQuery.error?.message ?? publishGuidance.error?.message ?? applyNow.error?.message ?? rejectGuidance.error?.message ?? null}
        onApplyNow={(itemId) => applyNow.mutate(itemId)}
        onReject={(itemId) => rejectGuidance.mutate(itemId)}
        onClose={() => setInspectorMode("node")}
        mutating={interactionPending}
      /> : <NodeInspector
        node={selectedNode}
        replaying={Boolean(selectedKey && replay?.activeKeys.has(selectedKey))}
        lineageEmpty={!lineage.nodes.length}
        onStopDirection={stopDirection}
        onStopActiveRuns={(runIds) => stopActiveRuns.mutate(runIds)}
        actionError={publishGuidance.error?.message ?? stopActiveRuns.error?.message ?? null}
        actionPending={interactionPending}
        readOnly={readOnly}
      />}
    </div>
  </section>;
}
